import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiPlus, FiArrowLeft, FiTrash2, FiUsers } from 'react-icons/fi';
import api from '../../config/api';
import { queryKeys, staleTimes } from '../../config/queryKeys';
import LoadingButton from '../LoadingButton';
import '../../pages/Requisitions.css';
import {
  TAX_ID_TYPE_OPTIONS,
  GST_TREATMENT_OPTIONS,
  defaultTaxIdTypeForCountry,
  taxIdLabel,
} from '../../utils/taxDocumentFields';
import { appAlert, appConfirm } from '../../utils/appDialog';
import ListPager from '../ListPager';
import { useClientPager } from '../../hooks/useClientPager';

const MAX_CLIENT_STAKEHOLDERS = 5;
const MAX_INTERNAL_CONTACTS = 5;

function emptyStakeholder() {
  return {
    user_id: '',
    full_name: '',
    email: '',
    phone: '',
    title: '',
    workflow_role_code: '',
    is_primary: false,
  };
}

function emptyInternalContact() {
  return {
    user_id: '',
    full_name: '',
    email: '',
    phone: '',
    title: '',
    workflow_role_code: '',
  };
}

function userLabel(u) {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  const role = u.role === 'external' ? 'external' : u.role;
  return `${name || u.email}${u.email && name ? ` · ${u.email}` : ''}${role ? ` (${role})` : ''}`;
}

function emptyCustomerForm() {
  return {
    billing_name: '',
    legal_name: '',
    division: '',
    country: '',
    country_code: '',
    state_province: '',
    address: '',
    billing_email: '',
    billing_phone: '',
    tax_id: '',
    tax_id_type: '',
    tax_exempt: false,
    gst_treatment: 'regular',
    place_of_supply: '',
    website: '',
    industry: '',
    notes: '',
    is_active: true,
    stakeholders: [emptyStakeholder()],
    internal_contacts: [emptyInternalContact()],
  };
}

/**
 * Consulting customers — billing clients, client stakeholders, and internal approvers.
 */
const ServicesCustomersPanel = ({ canManage, showTitle = true }) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [selectedId, setSelectedId] = useState(searchParams.get('id') || null);
  const [isCreating, setIsCreating] = useState(searchParams.get('new') === '1');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyCustomerForm());
  const [detailLoading, setDetailLoading] = useState(false);
  // Snapshot of the form as opened, so closing can warn about unsaved edits.
  const pristineFormRef = useRef(form);
  const detailOpen = Boolean(selectedId) || isCreating;

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Sync URL → view (deep links + browser back/forward)
  useEffect(() => {
    const id = searchParams.get('id');
    const isNew = searchParams.get('new') === '1';
    if (isNew) {
      if (!isCreating) {
        setIsCreating(true);
        setSelectedId(null);
        applyForm(emptyCustomerForm(), null);
      }
      return;
    }
    if (id) {
      if (String(selectedId) !== String(id) || String(editingId) !== String(id)) {
        setIsCreating(false);
        setSelectedId(String(id));
        setDetailLoading(true);
        api
          .get(`/services/customers/${id}`)
          .then((r) => {
            applyForm(formFromDetail(r.data), id);
          })
          .catch((e) => {
            void appAlert(e.response?.data?.error || e.message);
            setSearchParams({}, { replace: true });
            setSelectedId(null);
            setEditingId(null);
          })
          .finally(() => setDetailLoading(false));
      }
      return;
    }
    if (selectedId || isCreating) {
      setSelectedId(null);
      setIsCreating(false);
      setEditingId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { data: customers = [], isLoading, isError, error } = useQuery(
    queryKeys.serviceCustomersSearch(searchDebounced),
    () =>
      api
        .get('/services/customers', {
          params: {
            include_inactive: 'true',
            ...(searchDebounced ? { q: searchDebounced } : {}),
          },
        })
        .then((r) => r.data),
    { keepPreviousData: true, staleTime: staleTimes.lists, enabled: !detailOpen }
  );

  const {
    pageItems: pagedCustomers,
    pagination: customerPagination,
    setPage: setCustomerPage,
  } = useClientPager(customers, { resetKey: searchDebounced });

  const { data: currencies = [] } = useQuery(
    'currencies-for-customers',
    () => api.get('/currencies').then((r) => r.data),
    { enabled: detailOpen, staleTime: 5 * 60 * 1000 }
  );

  const { data: contactUsers = [] } = useQuery(
    ['customer-contact-users'],
    () => api.get('/services/customers/contact-users').then((r) => r.data),
    { enabled: detailOpen, staleTime: 60_000 }
  );

  const { data: workflowRoles = [] } = useQuery(
    ['workflow-roles', 'customer'],
    () => api.get('/workflow-roles').then((r) => r.data || []),
    { enabled: detailOpen, staleTime: 5 * 60 * 1000 }
  );

  const staffUsers = useMemo(
    () => (contactUsers || []).filter((u) => u.role !== 'external'),
    [contactUsers]
  );

  const countryOptions = useMemo(() => {
    const map = new Map();
    for (const row of currencies || []) {
      const code = row.country_code || '';
      const name = row.country || code;
      if (!name) continue;
      const key = code || name;
      if (!map.has(key)) map.set(key, { code, name });
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [currencies]);

  const invalidate = () => queryClient.invalidateQueries(queryKeys.serviceCustomers[0]);

  const saveMutation = useMutation(
    (payload) =>
      editingId
        ? api.put(`/services/customers/${editingId}`, payload)
        : api.post('/services/customers', payload),
    {
      onSuccess: (res) => {
        invalidate();
        const saved = res?.data;
        const id = saved?.id || editingId;
        setIsCreating(false);
        if (id) {
          setSelectedId(String(id));
          setSearchParams({ id: String(id) }, { replace: true });
          if (saved?.billing_name != null || saved?.stakeholders) {
            applyForm(formFromDetail(saved), id);
          } else {
            openRecord(id, { skipDirtyCheck: true });
          }
        } else {
          backToList({ skipDirtyCheck: true });
        }
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deleteMutation = useMutation((id) => api.delete(`/services/customers/${id}`), {
    onSuccess: () => {
      invalidate();
      backToList({ skipDirtyCheck: true });
    },
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const formDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(pristineFormRef.current),
    [form]
  );

  const applyForm = (nextForm, id = null) => {
    pristineFormRef.current = nextForm;
    setEditingId(id);
    setForm(nextForm);
  };

  const backToList = async ({ skipDirtyCheck = false } = {}) => {
    if (!skipDirtyCheck && formDirty && !await appConfirm('Discard unsaved customer changes?')) {
      return;
    }
    setIsCreating(false);
    setSelectedId(null);
    setEditingId(null);
    const fresh = emptyCustomerForm();
    pristineFormRef.current = fresh;
    setForm(fresh);
    setSearchParams({}, { replace: true });
  };

  const openCreate = async () => {
    if (formDirty && detailOpen && !await appConfirm('Discard unsaved customer changes?')) return;
    const fresh = emptyCustomerForm();
    applyForm(fresh, null);
    setIsCreating(true);
    setSelectedId(null);
    setSearchParams({ new: '1' }, { replace: true });
  };

  const formFromDetail = (detail) => {
    const all = detail.stakeholders || [];
    const clients = all.filter((s) => !s.is_internal);
    const internals = all.filter((s) => s.is_internal);
    return {
      billing_name: detail.billing_name || '',
      legal_name: detail.legal_name || '',
      division: detail.division || '',
      country: detail.country || '',
      country_code: detail.country_code || '',
      state_province: detail.state_province || '',
      address: detail.address || '',
      billing_email: detail.billing_email || '',
      billing_phone: detail.billing_phone || '',
      tax_id: detail.tax_id || '',
      tax_id_type: detail.tax_id_type || defaultTaxIdTypeForCountry(detail.country_code),
      tax_exempt: Boolean(detail.tax_exempt),
      gst_treatment: detail.gst_treatment || 'regular',
      place_of_supply: detail.place_of_supply || detail.state_province || '',
      website: detail.website || '',
      industry: detail.industry || '',
      notes: detail.notes || '',
      is_active: detail.is_active !== 0,
      stakeholders:
        clients.length > 0
          ? clients.map((s) => ({
              user_id: s.user_id ? String(s.user_id) : '',
              full_name: s.full_name || '',
              email: s.email || '',
              phone: s.phone || '',
              title: s.title || '',
              workflow_role_code: s.workflow_role_code || '',
              is_primary: Boolean(s.is_primary),
            }))
          : [emptyStakeholder()],
      internal_contacts:
        internals.length > 0
          ? internals.map((s) => ({
              user_id: s.user_id ? String(s.user_id) : '',
              full_name: s.full_name || '',
              email: s.email || '',
              phone: s.phone || '',
              title: s.title || '',
              workflow_role_code: s.workflow_role_code || '',
            }))
          : [emptyInternalContact()],
    };
  };

  const openRecord = async (id, { skipDirtyCheck = false } = {}) => {
    if (!skipDirtyCheck && formDirty && detailOpen && !await appConfirm('Discard unsaved customer changes?')) {
      return;
    }
    setIsCreating(false);
    setSelectedId(String(id));
    setSearchParams({ id: String(id) }, { replace: true });
    setDetailLoading(true);
    try {
      const detail = await api.get(`/services/customers/${id}`).then((r) => r.data);
      applyForm(formFromDetail(detail), id);
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
      backToList({ skipDirtyCheck: true });
    } finally {
      setDetailLoading(false);
    }
  };

  const updateStakeholder = (idx, patch) => {
    setForm((prev) => {
      const stakeholders = [...prev.stakeholders];
      stakeholders[idx] = { ...stakeholders[idx], ...patch };
      if (patch.is_primary) {
        for (let i = 0; i < stakeholders.length; i += 1) {
          if (i !== idx) stakeholders[i] = { ...stakeholders[i], is_primary: false };
        }
      }
      return { ...prev, stakeholders };
    });
  };

  const updateInternal = (idx, patch) => {
    setForm((prev) => {
      const internal_contacts = [...prev.internal_contacts];
      internal_contacts[idx] = { ...internal_contacts[idx], ...patch };
      return { ...prev, internal_contacts };
    });
  };

  const selectStakeholderUser = (idx, userId) => {
    if (!userId) {
      updateStakeholder(idx, { user_id: '', full_name: '', email: '', phone: '' });
      return;
    }
    const u = contactUsers.find((x) => String(x.id) === String(userId));
    if (!u) {
      updateStakeholder(idx, { user_id: userId });
      return;
    }
    updateStakeholder(idx, {
      user_id: String(u.id),
      full_name: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || '',
      email: u.email || '',
      phone: u.phone || '',
    });
  };

  const selectInternalUser = (idx, userId) => {
    if (!userId) {
      updateInternal(idx, { user_id: '', full_name: '', email: '', phone: '' });
      return;
    }
    const u = staffUsers.find((x) => String(x.id) === String(userId));
    if (!u) {
      updateInternal(idx, { user_id: userId });
      return;
    }
    updateInternal(idx, {
      user_id: String(u.id),
      full_name: [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || '',
      email: u.email || '',
      phone: u.phone || '',
    });
  };

  const submit = (e) => {
    e.preventDefault();
    const stakeholders = (form.stakeholders || [])
      .filter((s) => String(s.full_name || '').trim() || s.user_id)
      .slice(0, MAX_CLIENT_STAKEHOLDERS)
      .map((s, index) => ({
        user_id: s.user_id ? Number(s.user_id) : null,
        full_name: String(s.full_name || '').trim(),
        email: s.email || null,
        phone: s.phone || null,
        title: s.title || null,
        workflow_role_code: s.workflow_role_code || null,
        is_primary: s.is_primary || index === 0,
        is_internal: false,
      }));
    const internals = (form.internal_contacts || [])
      .filter((s) => s.user_id)
      .slice(0, MAX_INTERNAL_CONTACTS)
      .map((s) => ({
        user_id: Number(s.user_id),
        full_name: String(s.full_name || '').trim(),
        email: s.email || null,
        phone: s.phone || null,
        title: s.title || null,
        workflow_role_code: s.workflow_role_code || null,
        is_primary: false,
        is_internal: true,
      }));
    if (!form.billing_name.trim()) {
      void appAlert('Billing name is required');
      return;
    }
    if (!stakeholders.length) {
      void appAlert('Add at least one client stakeholder (pick a registered user or enter a name)');
      return;
    }
    if (!internals.length) {
      void appAlert('Add at least one internal contact for requisition approvals');
      return;
    }
    saveMutation.mutate({
      ...form,
      billing_name: form.billing_name.trim(),
      stakeholders: [...stakeholders, ...internals],
    });
  };

  const readOnly = !canManage;

  return (
    <div className="svc-customers">
      {!detailOpen ? (
        <>
          <div className="svc-panel-head">
            {showTitle ? (
              <div>
                <h2>Customers</h2>
                <p className="svc-panel-meta">
                  Consulting client records used for quotations, invoices, and requisition approvals.
                  Add client stakeholders plus internal contacts who approve work orders.
                </p>
              </div>
            ) : (
              <div />
            )}
            <div className="svc-sales-toolbar">
              <input
                type="search"
                placeholder="Search customers…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search customers"
              />
              {canManage && (
                <button type="button" className="btn btn-primary btn-sm" onClick={openCreate}>
                  <FiPlus /> Add customer
                </button>
              )}
            </div>
          </div>

          <div className="svc-table-panel svc-panel req-table-panel">
            {isError ? (
              <div className="error" role="alert">
                {error?.response?.data?.error || error?.message || 'Failed to load customers.'}
              </div>
            ) : isLoading ? (
              <div className="loading">Loading…</div>
            ) : customers.length === 0 ? (
              <div className="svc-empty-inline">
                <FiUsers aria-hidden />
                <p>No customers yet. Add a client to use them on quotations and invoices.</p>
              </div>
            ) : (
              <div className="svc-table-scroll">
                <table className="data-table req-table">
                  <thead>
                    <tr>
                      <th>Billing name</th>
                      <th>Legal name</th>
                      <th>Division</th>
                      <th>Country</th>
                      <th>Stakeholders</th>
                      <th>Internal approvers</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedCustomers.map((c) => (
                      <tr
                        key={c.id}
                        tabIndex={0}
                        role="link"
                        aria-label={`Open customer ${c.billing_name}`}
                        onClick={() => openRecord(c.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openRecord(c.id);
                          }
                        }}
                      >
                        <td className="req-cell-title">
                          <strong>{c.billing_name}</strong>
                          {c.billing_email ? (
                            <div className="svc-panel-meta">{c.billing_email}</div>
                          ) : null}
                        </td>
                        <td>{c.legal_name || '—'}</td>
                        <td>{c.division || '—'}</td>
                        <td>{c.country || c.country_code || '—'}</td>
                        <td>{c.stakeholder_names || '—'}</td>
                        <td>{c.internal_contact_names || '—'}</td>
                        <td>
                          <span
                            className={`svc-status svc-status--${c.is_active ? 'active' : 'void'}`}
                          >
                            {c.is_active ? 'active' : 'inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {customers.length > 0 ? (
              <ListPager
                page={customerPagination.page}
                pageCount={customerPagination.pageCount}
                total={customerPagination.total}
                limit={customerPagination.limit}
                onPageChange={setCustomerPage}
              />
            ) : null}
          </div>
        </>
      ) : (
        <section className="svc-panel req-detail-view cust-detail-view" aria-label="Customer details">
          {detailLoading ? (
            <p className="svc-empty">Loading…</p>
          ) : (
            <>
              <div className="req-detail-top">
                <div>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => backToList()}>
                    <FiArrowLeft /> Back to list
                  </button>
                  <h2>
                    {isCreating
                      ? 'New customer'
                      : form.billing_name || 'Customer'}
                  </h2>
                  <p className="svc-panel-meta">
                    {isCreating
                      ? 'Create billing details, stakeholders, and internal approvers.'
                      : form.legal_name && form.legal_name !== form.billing_name
                        ? `Legal: ${form.legal_name}`
                        : 'Edit customer details on this page.'}
                  </p>
                </div>
                <div className="req-detail-actions">
                  {!isCreating && form.is_active !== false && canManage ? (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={async () => {
                        if (!await appConfirm(`Deactivate customer “${form.billing_name}”?`)) return;
                        deleteMutation.mutate(editingId);
                      }}
                    >
                      <FiTrash2 /> Deactivate
                    </button>
                  ) : null}
                  <span className={`svc-status svc-status--${form.is_active ? 'active' : 'void'}`}>
                    {form.is_active ? 'active' : 'inactive'}
                  </span>
                </div>
              </div>

      <form onSubmit={submit} className="svc-package-form">
        <fieldset disabled={readOnly} className="cust-detail-fieldset">
        <div className="svc-form-grid svc-form-grid--2">
          <div className="form-group">
            <label htmlFor="cust-billing-name">Billing name</label>
            <input
              id="cust-billing-name"
              value={form.billing_name}
              onChange={(e) => setForm({ ...form, billing_name: e.target.value })}
              required
              placeholder="Acme Corp"
            />
          </div>
          <div className="form-group">
            <label>Legal name</label>
            <input
              value={form.legal_name}
              onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
              placeholder="Acme Corporation Pvt Ltd"
            />
          </div>
        </div>

        <div className="svc-form-grid svc-form-grid--3">
          <div className="form-group">
            <label>Division</label>
            <input
              value={form.division}
              onChange={(e) => setForm({ ...form, division: e.target.value })}
              placeholder="Engineering / APAC"
            />
          </div>
          <div className="form-group">
            <label>Country</label>
            <select
              value={form.country_code || ''}
              onChange={(e) => {
                const code = e.target.value;
                const opt = countryOptions.find((o) => o.code === code);
                setForm({
                  ...form,
                  country_code: code,
                  country: opt?.name || form.country,
                });
              }}
            >
              <option value="">Select country…</option>
              {countryOptions.map((o) => (
                <option key={o.code || o.name} value={o.code}>
                  {o.name}
                </option>
              ))}
              {form.country_code &&
                !countryOptions.some((o) => o.code === form.country_code) && (
                  <option value={form.country_code}>{form.country || form.country_code}</option>
                )}
            </select>
          </div>
          <div className="form-group">
            <label>Industry</label>
            <input
              value={form.industry}
              onChange={(e) => setForm({ ...form, industry: e.target.value })}
              placeholder="Technology"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Address</label>
          <textarea
            rows={3}
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="Street, city, postal code"
          />
        </div>

        <div className="svc-form-grid svc-form-grid--3">
          <div className="form-group">
            <label>Billing email</label>
            <input
              type="email"
              value={form.billing_email}
              onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Billing phone</label>
            <input
              value={form.billing_phone}
              onChange={(e) => setForm({ ...form, billing_phone: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>State / province</label>
            <input
              value={form.state_province}
              onChange={(e) =>
                setForm({
                  ...form,
                  state_province: e.target.value.toUpperCase(),
                  place_of_supply: form.place_of_supply || e.target.value.toUpperCase(),
                })
              }
              placeholder="CA / ON / MH"
            />
          </div>
        </div>

        <div className="svc-form-grid svc-form-grid--3">
          <div className="form-group">
            <label>Tax ID type</label>
            <select
              value={form.tax_id_type || defaultTaxIdTypeForCountry(form.country_code)}
              onChange={(e) => setForm({ ...form, tax_id_type: e.target.value })}
            >
              {TAX_ID_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{taxIdLabel(form.tax_id_type || defaultTaxIdTypeForCountry(form.country_code))}</label>
            <input
              value={form.tax_id}
              onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
              placeholder="Printed on invoices"
            />
          </div>
          <div className="form-group">
            <label>Place of supply</label>
            <input
              value={form.place_of_supply}
              onChange={(e) => setForm({ ...form, place_of_supply: e.target.value.toUpperCase() })}
              placeholder="State / province code"
            />
          </div>
        </div>

        <div className="svc-form-grid svc-form-grid--2">
          <div className="form-group">
            <label>GST / tax treatment</label>
            <select
              value={form.gst_treatment || 'regular'}
              onChange={(e) => setForm({ ...form, gst_treatment: e.target.value })}
            >
              {GST_TREATMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Tax exempt</label>
            <select
              value={form.tax_exempt ? '1' : '0'}
              onChange={(e) => setForm({ ...form, tax_exempt: e.target.value === '1' })}
            >
              <option value="0">No</option>
              <option value="1">Yes (certificate held externally)</option>
            </select>
          </div>
        </div>

        <div className="svc-form-grid svc-form-grid--2">
          <div className="form-group">
            <label>Website</label>
            <input
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://"
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={form.is_active ? '1' : '0'}
              onChange={(e) => setForm({ ...form, is_active: e.target.value === '1' })}
            >
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Notes</label>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        <div className="svc-form-section-head">
          <h3>Client stakeholders / Leads (1–{MAX_CLIENT_STAKEHOLDERS})</h3>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={form.stakeholders.length >= MAX_CLIENT_STAKEHOLDERS}
            onClick={() =>
              setForm({
                ...form,
                stakeholders: [...form.stakeholders, emptyStakeholder()],
              })
            }
          >
            <FiPlus /> Add lead
          </button>
        </div>
        <p className="svc-form-hint">
          Customer-side contacts for sales and billing. Assign a workflow role (Reviewer,
          Approver, Signatory) from Metadata — separate from app login roles. Mark one as primary.
        </p>
        <div className="svc-sales-lines">
          {form.stakeholders.map((s, idx) => (
            <div key={`stake-${idx}`} className="svc-customer-stakeholder-row">
              <div className="svc-form-grid svc-form-grid--2">
                <div className="form-group">
                  <label>App user</label>
                  <select
                    value={s.user_id || ''}
                    onChange={(e) => selectStakeholderUser(idx, e.target.value)}
                  >
                    <option value="">— Manual / not linked —</option>
                    {contactUsers.map((u) => (
                      <option key={u.id} value={String(u.id)}>
                        {userLabel(u)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Workflow role</label>
                  <select
                    value={s.workflow_role_code || ''}
                    onChange={(e) =>
                      updateStakeholder(idx, { workflow_role_code: e.target.value })
                    }
                  >
                    <option value="">— None —</option>
                    {workflowRoles.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="svc-form-grid svc-form-grid--4">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    value={s.full_name}
                    onChange={(e) =>
                      updateStakeholder(idx, { full_name: e.target.value, user_id: s.user_id })
                    }
                    placeholder="Jane Doe"
                    required={!s.user_id}
                  />
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    value={s.title}
                    onChange={(e) => updateStakeholder(idx, { title: e.target.value })}
                    placeholder="VP Engineering"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={s.email}
                    onChange={(e) => updateStakeholder(idx, { email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    value={s.phone}
                    onChange={(e) => updateStakeholder(idx, { phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="svc-customer-stakeholder-actions">
                <label className="svc-checkbox-label">
                  <input
                    type="checkbox"
                    checked={Boolean(s.is_primary)}
                    onChange={(e) => updateStakeholder(idx, { is_primary: e.target.checked })}
                  />
                  Primary
                </label>
                {form.stakeholders.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() =>
                      setForm({
                        ...form,
                        stakeholders: form.stakeholders.filter((_, i) => i !== idx),
                      })
                    }
                  >
                    <FiTrash2 /> Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="svc-form-section-head">
          <h3>Internal contacts (1–{MAX_INTERNAL_CONTACTS})</h3>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={form.internal_contacts.length >= MAX_INTERNAL_CONTACTS}
            onClick={() =>
              setForm({
                ...form,
                internal_contacts: [...form.internal_contacts, emptyInternalContact()],
              })
            }
          >
            <FiPlus /> Add contact
          </button>
        </div>
        <p className="svc-form-hint">
          Staff users for this customer&apos;s Req workflow. Role Reviewer is notified at Review;
          Approver at Approval. Manage role labels under Metadata → Workflow roles.
        </p>
        <div className="svc-sales-lines">
          {form.internal_contacts.map((s, idx) => (
            <div key={`internal-${idx}`} className="svc-customer-stakeholder-row">
              <div className="svc-form-grid svc-form-grid--2">
                <div className="form-group">
                  <label>Staff user</label>
                  <select
                    aria-label="Staff user"
                    value={s.user_id || ''}
                    onChange={(e) => selectInternalUser(idx, e.target.value)}
                    required
                  >
                    <option value="">Select staff user…</option>
                    {staffUsers.map((u) => (
                      <option key={u.id} value={String(u.id)}>
                        {userLabel(u)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Workflow role</label>
                  <select
                    value={s.workflow_role_code || ''}
                    onChange={(e) => updateInternal(idx, { workflow_role_code: e.target.value })}
                  >
                    <option value="">— None —</option>
                    {workflowRoles.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="svc-form-grid svc-form-grid--3">
                <div className="form-group">
                  <label>Name</label>
                  <input value={s.full_name} readOnly placeholder="Filled from user" />
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    value={s.title}
                    onChange={(e) => updateInternal(idx, { title: e.target.value })}
                    placeholder="Account Lead"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={s.email} readOnly />
                </div>
              </div>
              <div className="svc-customer-stakeholder-actions">
                {form.internal_contacts.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() =>
                      setForm({
                        ...form,
                        internal_contacts: form.internal_contacts.filter((_, i) => i !== idx),
                      })
                    }
                  >
                    <FiTrash2 /> Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        </fieldset>
        {canManage ? (
          <div className="cust-detail-actions">
            <button type="button" className="btn btn-secondary" onClick={() => backToList()}>
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={saveMutation.isLoading}
              loadingLabel="Saving…"
            >
              {editingId ? 'Save customer' : 'Create customer'}
            </LoadingButton>
          </div>
        ) : null}
      </form>

            </>
          )}
        </section>
      )}
    </div>
  );
};

export default ServicesCustomersPanel;
